/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
// Ember Imports
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { TrackedArray } from 'tracked-built-ins';
import { get, action, computed } from '@ember/object';
import { setDiff, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { later, cancel } from '@ember/runloop';
import { isEmpty, typeOf } from '@ember/utils';

// External Imports
import config from '@linc-technologies/linced-common/utils/config';
import moment from 'moment';
import { observes } from '@ember-decorators/object';

// ember-auto-import flatpickr css.
import 'flatpickr/dist/themes/material_blue.css';

// Internal Imports
import GoalState from '@linc-technologies/linced-common/enums/goal-state';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import MediaSource from '@linc-technologies/linced-common/enums/media-source';
import PageType from '@linc-technologies/linced-common/enums/page-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import {
  filterBy,
  findBy,
  mapBy,
  uniq,
} from '@linc-technologies/linced-common/utils/arrays';
import { isMobile } from '@linc-technologies/linced-common/utils/platform';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import {
  autosaveCreateEditorId,
  autosaveTinyMCEConfig,
  manageAutosaveDraftsButton,
  removeAutosavedDrafts,
} from '@linc-technologies/linced-common/utils/tinymce';
import {
  copyAttachment,
  onAttachmentUpload,
} from '@linc-technologies/linced-common/utils/uppy';
import { snapshotFields } from '@linc-technologies/linced-common/utils/record-rollback';
import {
  mediaPostKey,
  saveWithMedia,
} from '@linc-technologies/linced-common/utils/post-media';

// txtMaxLength defines the maximum number of characters allowed for a text
// post.
const txtMaxLength = 130;

export default class NewPost extends Component {
  @action
  setNewDate(value) {
    this.newDate = value;
  }

  @action
  setPostBody(value) {
    this.postBody = value;
  }

  @action
  setStep(value) {
    this.step = value;
  }

  @action
  setSurvey(value) {
    this.survey = value;
  }

  @tracked
  newDate;

  // Component versioning
  @tracked visualStyle = 'v1';

  // Attrs
  txtMaxLength = txtMaxLength;

  // Services
  @service
  googleDrive;

  @service
  options;

  @service
  paperToaster;

  @service
  permissions;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  @service
  uppy;

  @tracked
  isMobile = false;

  PostType = PostType;

  dialogOpen = false;

  @tracked
  assessment = null;

  @tracked
  postImmediately = false;

  doNotOverwriteAttachments = false;

  selectMultiple = false;

  @tracked
  selectedPeople = [];

  @computed('selectedPeople.[]')
  get remainingPeople() {
    let allPeople = this.store
      .peekAll('person')
      .filter((person) => person.type === 1);
    return allPeople.filter((source) => {
      return !this.selectedPeople.some(function (person) {
        if (!person) {
          return false;
        }
        return source.id === person.id;
      });
    });
  }

  @observes('dialogOpen')
  _watchOpen() {
    if (this.dialogOpen) {
      this.openDialog();
    }
  }

  @tracked school = null;

  @computed('school', 'session.school')
  get computedSchool() {
    if (!isEmpty(this.school)) {
      return this.school;
    }

    return this.get('session.school');
  }

  @alias('computedSchool.heroDuoEnabled')
  heroDuoEnabled;

  // isAdminApp determines whether this is being rendered by the admin app.
  isAdminApp = config.modulePrefix === 'linced-admin';

  @tracked
  saveInProgress = false;

  @tracked
  step = 'post';

  @tracked
  postDate = moment().unix();

  @tracked postType = PostType.POST;

  get entityType() {
    switch (this.postType) {
      case PostType.GLOBAL_NOTICE:
        return 'posts/globalnotice';
      case PostType.COMMUNITY_NOTICE:
        return 'posts/communitynotice';
      case PostType.STUDENT_NOTICE:
        return 'posts/studentnotice';
      case PostType.STAFF_NOTICE:
        return 'posts/staffnotice';
      case PostType.TEXT:
        return 'posts/text';
      default:
        return 'posts/post';
    }
  }

  get postTypeTitle() {
    return PostType.getLabel(this.postType).replace(' Notice', ' Post');
  }

  @tracked type = 1;

  @tracked attachments = [];

  get filteredPeople() {
    let type = this.type;
    if (type > 2) {
      return [];
    }

    return this.store
      .peekAll('person')
      .filter((person) => person.type === type);
  }

  get filteredPages() {
    let type = this.type;
    return this.store
      .peekAll('posts/page')
      .filter((page) => page.type === type && page.visibleInMenu)
      .sort(
        (a, b) =>
          a.sequence - b.sequence ||
          (a.label || '').localeCompare(b.label || ''),
      );
  }

  @computed('session.scopes.[]')
  get userCanTag() {
    // Caregivers cannot tag Posts.  All other users can (if the Page allows it).
    return !this.permissions.equal('urn:linced:organisation:role:caregiver');
  }

  // Plain getter, not @computed: allGoals is TrackedArray-backed and
  // mutated asynchronously in place, and a classic @computed('allGoals.[]')
  // dependent key doesn't reliably observe that. Reading this.allGoals.length
  // directly here autotracks it correctly instead.
  get pageHasTags() {
    if (!this.userCanTag) {
      // User not allowed to tag Posts - don't bother checking the Page settings.
      return false;
    }

    // User is allowed to tag Posts - evaluate page options to determine whether tags (or goals as tags) are enabled.
    let hasCurrentGoals =
      this.get('selectedPage.hasGoalsAsTags') && this.allGoals.length > 0;
    let hasTags = this.get('selectedPage.tagCategoryIds.length') > 0;
    return hasCurrentGoals || hasTags;
  }

  @tracked
  postTags = [];

  @computed('selectedPage.tagCategoryIds.[]')
  get tagListClasses() {
    if (!this.pageHasTags) {
      return 'flex';
    }
    let numCategories = this.get('selectedPage.tagCategoryIds.length');
    switch (numCategories) {
      case 1:
        return 'flex-100';
      case 2:
        return 'flex-xs-100 flex-gt-xs-45';
      case 3:
        return 'flex-xs-100 flex-gt-xs-30';
      case 4:
        return 'flex-xs-100 flex-gt-xs-30 flex-gt-md-25';
      default:
        // 5 or more categories - limit to 5 per row
        return 'flex-xs-100 flex-gt-xs-30 flex-gt-md-20';
    }
  }

  get showGroups() {
    return [
      PostType.GLOBAL_NOTICE,
      PostType.COMMUNITY_NOTICE,
      PostType.STUDENT_NOTICE,
      PostType.TEXT,
    ].includes(this.postType);
  }

  @computed('session.{activeSchool,personId}')
  get filteredGroups() {
    // By default, return an empty array.
    // Consuming apps can override this computed property if they should offer a list of groups (i.e. main app).
    return [];
  }

  @tracked
  selectedGroups = [];

  @setDiff('filteredGroups', 'selectedGroups')
  remainingGroups;

  @computed('remainingGroups')
  get sortedRemainingGroups() {
    let groupTypes = uniq(mapBy(this.remainingGroups, 'type')).sort();
    let sortedRemainingGroups = [];
    groupTypes.forEach((groupType) => {
      let semiFilteredGroups = filterBy(
        this.remainingGroups,
        'type',
        groupType,
      );
      let groupings = uniq(mapBy(semiFilteredGroups, 'grouping')).sort();
      groupings.forEach((grouping) => {
        sortedRemainingGroups.push(
          ...filterBy(semiFilteredGroups, 'grouping', grouping).sort(
            naturalSort('label'),
          ),
        );
      });
    });
    return sortedRemainingGroups;
  }

  @computed(
    'selectedGroups.[]',
    'selectedPage',
    'selectedPerson',
    'postBody',
    'type',
  )
  get formIsInvalid() {
    let postType = this.postType;
    switch (postType) {
      case PostType.POST:
        if (!this.selectedPage) {
          // Require a post type (page) be selected for regular Posts
          return true;
        }

        if (
          this.type <= 2 &&
          !this.selectedPerson &&
          isEmpty(this.selectedPeople[0])
        ) {
          // Require a person be chosen (except for site-wide posts)
          return true;
        }
        break;

      case PostType.COMMUNITY_NOTICE:
      case PostType.STUDENT_NOTICE:
        if (this.selectedGroups.length === 0) {
          // A Community or Student Notice requires at least one group in order for
          // notifications to be sent.
          return true;
        }
        break;

      case PostType.TEXT:
        if (this.selectedGroups.length === 0) {
          // A Text Notice requires at least one group in order for
          // notifications to be sent.
          return true;
        }

        if (this.postBody.length > this.txtMaxLength) {
          // Text messages are character limited.
          return true;
        }
        break;
    }

    let postBody = this.postBody;
    if (!postBody || postBody === '<p></p>') {
      return true;
    }

    return false;
  }

  @tracked postBody = '';

  // Goals
  @tracked
  goalSubjects = [];

  @tracked
  selectedGoals = [];

  @computed(
    'userCanTag',
    'selectedPage.hasGoalsAsTags',
    'selectedPerson.id',
    'post.goals.[]',
  )
  get allGoals() {
    let store = this.store;
    // TrackedArray, not a plain array: the .then() callbacks below mutate
    // this specific returned instance in place, well after this getter has
    // already returned (and after pageHasTags, which depends on
    // 'allGoals.[]', has already read it) - that needs to stay reactive.
    let allGoals = new TrackedArray();

    // Don't try to fetch Goals if they're not enabled (or the user isn't allowed to tag Posts).
    if (!this.userCanTag || !this.selectedPage.hasGoalsAsTags) {
      return allGoals;
    }

    store
      .query('goals/state', {
        personId: this.get('selectedPerson.id'),
        current: true,
        awarded: GoalState.DEVELOPING,
        include: 'goals',
      })
      .then((states) => {
        states.forEach((state) => {
          state.get('goal').then((goal) => {
            // Don't add double-ups, if this resolves after the already-selected goals are pushed in.
            if (!allGoals.includes(goal)) {
              allGoals.push(goal);
            }
          });
        });

        // Add any already-selected goals which the learner is no longer working on.
        // This allows them to be removed if desired.
        let postGoals = this.get('post.goals');
        if (!isEmpty(postGoals)) {
          postGoals.forEach((selected) => {
            // Don't add double-ups, if we've already added it from the learner's current goals.
            if (!allGoals.includes(selected)) {
              allGoals.push(selected);
            }
          });
        }
      });

    return allGoals;
  }

  // Surveys
  @tracked
  survey = null;

  @tracked
  surveyDialogOpen = false;

  @tracked
  allSurveys = [];

  // TinyMCE editor
  // editorOptions returns an object for TinyMCE editor config with autosave enabled.
  // We need a unique element ID for specifying selectors and localStorage keys.
  @computed('editorId')
  get editorOptions() {
    return Object.freeze(autosaveTinyMCEConfig(this.editorId));
  }

  // editorId returns an element ID to assign to the editor's children
  @computed(
    'session.person.id',
    'selectedPersonIdPrefix',
    'selectedPeoplePrefix',
    'additionalAutosavePrefix',
  )
  get editorId() {
    let userId = this.session.person.get('id');

    return autosaveCreateEditorId(
      userId,
      this.selectedPersonIdPrefix,
      this.additionalAutosavePrefix,
    );
  }

  // the following prefixes are used in editorId
  // if a parent component or route adds it for more uniqueness.
  @tracked selectedPersonIdPrefix = '';

  @tracked additionalAutosavePrefix = '';

  // allowedFileTypes lists what the attachment picker accepts, sourced from the
  // uppy service rather than restated here, so this can't drift from the
  // restrictions the picker actually enforces.
  @computed('heroDuoEnabled')
  get allowedFileTypes() {
    let allowed = [...this.uppy.allowedFileTypes];
    if (this.heroDuoEnabled) {
      allowed.push(...this.uppy.allowedVideoTypes);
    }

    return allowed;
  }

  finishEditing() {}

  @computed('session')
  get plpLabel() {
    return (
      this.options.schoolGet(
        this.session.school,
        'core:studentGoalSetting:plpFullLabel',
      ) || 'Personal Learning Plan'
    );
  }

  @action
  cancel() {
    this.set('dialogOpen', false);
    this.reset();

    this.finishEditing();
  }

  @action
  handleCreateMediaPost(file, upload, key, type) {
    let record = this.store.createRecord('media', {
      schoolId: this.computedSchool,
      personId: this.get('session.person'),
      sharedWith: SharingLevel.SCHOOL,
      mediaType: type,
      mediaSource: MediaSource.INTERNAL,
      mimeType: file.type,
      fileName: file.name,
      storageKey: key,
      fileSize: file.size,
    });

    this.attachments = [...this.attachments, { file, upload, record }];
    this.initialisePostData();
    this.savePost();
  }

  @action
  addPerson(learner) {
    this.selectedPeople = [...this.selectedPeople, learner];
  }

  @action
  removePerson(learner) {
    this.selectedPeople = this.selectedPeople.filter(
      (person) => person !== learner,
    );
  }

  /**
   * Save the post to LincEd.
   */
  @action
  async savePost() {
    this.saveInProgress = true;
    let post = this.post;
    let restorePost;
    try {
      if (post) {
        // Update existing post
        let attachments = this.attachments;
        let media = post.get('media');
        let mediaToRemove = [];
        let attachmentPromises = [];

        // Remember what the post held, so a failed save can put it back. A
        // reload no longer discards unsaved relationship edits, so without this
        // they would show on the post and be sent by its next save.
        restorePost = snapshotFields(post, [
          'pageId',
          'personId',
          'groupIds',
          'subject',
          'displayDate',
          'content',
          'tagIds',
          'surveyId',
          'goalIds',
        ]);

        post.set('page', this.selectedPage);
        post.set('personId', this.selectedPerson);
        post.set('groupIds', this.selectedGroups);
        post.set('subject', this.postSubject);
        post.set('displayDate', this.postDate);
        post.set('content', this.postBody);
        post.set('tagIds', this.postTags);
        post.set('surveyId', this.survey);

        if (this.get('selectedPage.hasGoalsAsTags')) {
          post.set('goalIds', this.selectedGoals);
        }

        media.forEach(function (record) {
          if (!findBy(attachments, 'record.id', record.get('id'))) {
            mediaToRemove.push(record);
          }
        });
        mediaToRemove.forEach(function (record) {
          record.destroyRecord();
        });

        attachments.forEach(function (attachment) {
          if (attachment.record.get('isNew')) {
            attachment.record.set(mediaPostKey(post), post);
            attachmentPromises.push(attachment.record.save());
          }
        });

        // Save post once any new attachments have been saved (so that we have their IDs).
        await Promise.all(attachmentPromises);
        await post.save();

        this.set('dialogOpen', false);
        this.reset();

        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);

        this.finishEditing();

        this.paperToaster.show(`Post updated successfully.`, {
          duration: 4000,
          position: 'top right',
        });
      } else if (this.selectMultiple) {
        // Create a new post
        let selectedPeople = this.selectedPeople;
        // Bulk post creation. Each post shows its own error and does not
        // stop the others, so this does not reject.
        await Promise.all(
          selectedPeople.map(async (person, index) => {
            // Each post gets its own media records. Copy them now, before any
            // save can fail and unload an original another post copies from.
            let media = this.attachments.map(({ record }) =>
              index === 0 ? record : copyAttachment(this.store, record),
            );
            // Create a new post
            let newPost = this.store.createRecord(this.entityType, {
              author: this.get('session.person'),
              schoolId: this.computedSchool,
              assessment: this.assessment,
              page: this.selectedPage,
              personId: person,
              groupIds: this.selectedGroups,
              subject: this.postSubject,
              displayDate: this.postDate,
              content: this.postBody,
              tagIds: this.postTags,
              status: PostStatus.DRAFT,
              surveyId: this.survey,
              eventId: this.event,
            });
            try {
              await saveWithMedia(newPost, media, () =>
                this.toaster.error('Error generating attachments'),
              );
            } catch {
              // The server has no post. Remove it and the media copied for it.
              newPost.unloadRecord();
              if (index > 0) {
                for (let record of media) {
                  record.unloadRecord();
                }
              }
              this.toaster.error('Error generating posts');
            }
          }),
        );

        let completedPosts = selectedPeople.length;
        this.set('dialogOpen', false);
        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);
        this.reset();
        this.toaster.success(`${completedPosts} Posts Successfully Created`);
        let postCreated = this.postCreated;
        if (postCreated) {
          postCreated();
        }
      } else {
        post = this.store.createRecord(this.entityType, {
          author: this.get('session.person'),
          schoolId: this.computedSchool,
          assessment: this.assessment,
          page: this.selectedPage,
          personId: this.selectedPerson,
          groupIds: this.selectedGroups,
          subject: this.postSubject,
          displayDate: this.postDate,
          content: this.postBody,
          tagIds: this.postTags,
          status: PostStatus.DRAFT,
          surveyId: this.survey,
          eventId: this.event,
        });

        if (this.get('selectedPage.hasGoalsAsTags')) {
          post.set('goalIds', this.selectedGoals);
        }

        // Auto-publish staff notices
        if (this.postType === PostType.STAFF_NOTICE) {
          post.set('status', PostStatus.PUBLISHED);
        }

        // Auto-publish posts on School pages
        if (this.selectedPage?.type === PageType.SCHOOL) {
          post.set('status', PostStatus.PUBLISHED);
        }

        await saveWithMedia(
          post,
          this.attachments.map(({ record }) => record),
          // The post is already created, so the form closes as usual (saving
          // it again would create a second post); say how to add the file.
          () =>
            this.toaster.error(
              'Post saved, but an attachment failed to upload. Edit the post to add it again.',
            ),
        );

        this.set('dialogOpen', false);
        this.reset();

        // Remove autosaves from the local storage
        let path = window.location.pathname;
        removeAutosavedDrafts(path, this.editorId);

        let postCreated = this.postCreated;
        if (postCreated) {
          postCreated(post);
        } else {
          if (this.postType === PostType.POST) {
            this.router.transitionTo(
              `/people/${post.get('person.id')}/posts/${post.id}`,
            );
          }
        }
      }
    } catch {
      if (restorePost) {
        // The dialog still holds the edits, so saving again reapplies them.
        restorePost();
        // Unlink attachments that didn't save, or the post's next save
        // sends their null ids. A retry links them again.
        for (let { record } of this.attachments) {
          if (record.isNew) {
            record.set(mediaPostKey(post), null);
          }
        }
      } else if (post?.isNew) {
        // The first save failed, so the server has no post. A retry makes a
        // new record, so remove this one.
        post.unloadRecord();
      }
      this.paperToaster.show(`Error saving post!`, {
        duration: 4000,
        position: 'top right',
      });
    } finally {
      this.saveInProgress = false;
    }
  }

  @action
  addImages() {
    this.uppy.addImages();
  }

  @action
  addVideos() {
    this.uppy.addVideos();
  }

  @action
  addFiles() {
    this.uppy.addFiles();
  }

  @action
  removeAttachment(attachment) {
    this.attachments = this.attachments.filter((a) => a !== attachment);
  }

  @action
  selectPerson(person) {
    this.selectedPerson = person;
  }

  @action
  clearPerson() {
    this.selectedPerson = null;
    this.postTags = [];
    this.selectedGoals = [];
  }

  @action
  selectPage(page) {
    this.selectedPage = page;
  }

  @action
  clearPage() {
    this.selectedPage = null;
    this.postTags = [];
    this.selectedGoals = [];
  }

  @action
  setPostDate(newDate) {
    this.postDate = moment(newDate[0]).unix();
  }

  // Groups
  @action
  addGroup(group) {
    this.selectedGroups = [...this.selectedGroups, group];
  }

  @action
  removeGroup(group) {
    this.selectedGroups = this.selectedGroups.filter((g) => g !== group);
  }

  // Goals
  @action
  toggleGoal(goal) {
    let goals = this.selectedGoals;
    if (goals.includes(goal)) {
      this.selectedGoals = goals.filter((g) => g !== goal);
    } else {
      this.selectedGoals = [...goals, goal];
    }
  }

  @action
  toggleTag(tag) {
    let tags = this.postTags;
    if (tags.includes(tag)) {
      this.postTags = tags.filter((t) => t !== tag);
    } else {
      this.postTags = [...tags, tag];
    }
  }

  /**
   * Launch's the Google Drive picker...
   * @param {*} postImmediately - if set to true, then automatically post to the feed once complete.
   */
  @action
  launchGooglePicker(postImmediately = false) {
    window.launchPickerCallback = this;

    this.postImmediately = postImmediately;
    this.googleDrive
      .pickFiles()
      .then((files) => this.embedDriveFiles(files))
      .catch((err) => this.googleDrive.showEmbedError(err));
  }

  @action
  closeGoogleDrivePermissionDialog(action) {
    if (typeOf(action) === 'function') {
      action();
    }
    this.googleDrivePermissionDialogOpen = false;
  }

  // Surveys
  @action
  openSurveyDialog() {
    // Kick off a request to fetch all surveys, then open the dialog.
    // findAll gives a plain Promise. Keep the resolved records, so that
    // the dialog search can filter them.
    this.store.findAll('surveys/survey').then((surveys) => {
      if (!this.isDestroying && !this.isDestroyed) {
        this.allSurveys = surveys;
      }
    });
    this.surveyDialogOpen = true;
  }

  @action
  closeSurveyDialog() {
    this.surveyDialogOpen = false;
  }

  async initialisePostData() {
    let person = this.person;
    let page = this.page;
    if (person && person.get('id')) {
      this.selectedPerson = person;
    }
    this.postDate = moment().unix();
    if (page && page.get('id')) {
      this.selectedPage = page;
      this.postTags = [];
      this.selectedGoals = [];
      // If a post template exists, set it as the post body content.
      this.postBody = get(page, 'postTemplate');
      if (this.content) {
        this.postBody = this.content;
      }
    }
    if (this.get('event.groupIds')) {
      let groups = await this.get('event.groupIds');
      this.selectedGroups = groups.slice();
    }
  }

  async openDialog() {
    let post = this.post;
    if (!this.doNotOverwriteAttachments) {
      this.attachments = [];
    }
    this.initUppy();

    if (post) {
      this.postSubject = post.get('subject');
      this.postBody = post.get('content');

      let postDate = post.get('date');
      if (postDate) {
        this.postDate = postDate;
      }

      // Await the relations rather than reading the PromiseManyArrays: the
      // groups, goals and tags are saved back with the post, so they must be
      // fully loaded, not whatever has resolved so far.
      // A belongsTo takes a record, not the async proxy. Only notices have an
      // event.
      let { person, page, survey, event, groups, goals, tags, media } =
        await hash({
          person: post.personId,
          page: post.pageId,
          survey: post.surveyId,
          event: post.eventId,
          groups: post.groupIds,
          goals: post.goalIds,
          tags: post.tagIds,
          media: post.media,
        });

      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      this.selectedPerson = person;
      this.selectedPage = page;
      this.survey = survey;
      this.event = event;
      this.selectedGroups = groups.slice();
      this.selectedGoals = goals.slice();
      this.postTags = tags.slice();

      let newAttachments = media.map((record) => ({ record }));
      this.attachments = [...this.attachments, ...newAttachments];
    } else {
      await this.initialisePostData();
      if (this.isDestroying || this.isDestroyed) {
        return;
      }
    }

    // Fetch all Goal Subjects, if we might need them for displaying Goals as tags.
    if (this.userCanTag) {
      this.store.findAll('goals/subject').then((subjects) => {
        this.goalSubjects = subjects;
      });
    }

    this.set('dialogOpen', true);
  }

  reset() {
    this.assessment = null;
    this.step = 'post';
    this.selectedPage = null;
    this.selectedPerson = null;
    this.postDate = moment().unix();
    this.postSubject = '';
    this.postBody = '';
    this.survey = null;
    this.selectedPeople = [];
    this.attachments = [];
    this.selectedGroups = [];
    this.selectedGoals = [];
    this.postTags = [];
  }

  didInsertElement() {
    super.didInsertElement(...arguments);

    this.initUppy();

    this.isMobile = isMobile();

    // TinyMCE autosave plugin
    // Manage the button's state on initial insertion
    manageAutosaveDraftsButton(this.editorId);
  }

  willUpdate() {
    super.willUpdate(...arguments);
    // TinyMCE autosave plugin
    // Manage the button's state on initial update
    manageAutosaveDraftsButton(this.editorId);
  }

  didUpdate() {
    super.didUpdate(...arguments);

    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
    this.timer = later(
      this,
      function () {
        manageAutosaveDraftsButton(this.editorId);
      },
      3000,
    );
  }

  willDestroy() {
    super.willDestroy(...arguments);
    // Cancel any pending timer.
    if (!isEmpty(this.timer)) {
      cancel(this.timer);
    }
  }

  @tracked
  googleDrivePermissionDialogOpen = false;

  @tracked
  googleDrivePermissionStatus = null;

  @tracked
  googleDrivePermissionResolve = null;

  @tracked
  googleDrivePermissionReject = null;

  @tracked
  event;

  @tracked
  postSubject;

  @action
  setPostSubject(value) {
    this.postSubject = value;
  }

  @tracked
  selectedPage;

  @tracked
  selectedPerson;

  @tracked
  timer;

  // embedDriveFiles adds the picked Google Drive files as attachments.
  embedDriveFiles(files) {
    for (const file of files) {
      this.googleDrive
        .embedFile(file, (status) => {
          // The user can still embed the file in the permission dialog.
          return new Promise((resolve, reject) => {
            this.googleDrivePermissionDialogOpen = true;
            this.googleDrivePermissionStatus = status;
            this.googleDrivePermissionResolve = resolve;
            // Closing the dialog is a cancel, which needs no message.
            this.googleDrivePermissionReject = () =>
              reject({ userCancelled: true });
          });
        })
        .then((media) => {
          if (!media) {
            return;
          }

          let record = this.store.createRecord('media', {
            ...media,
            schoolId: this.computedSchool,
            personId: this.get('session.person'),
            sharedWith: SharingLevel.SCHOOL,
          });

          this.attachments = [...this.attachments, { record }];

          if (this.postImmediately) {
            // Save the post immediately...
            this.initialisePostData();
            this.savePost();
          }
        })
        .catch((err) => this.googleDrive.showEmbedError(err, file));
    }
  }

  initUppy() {
    this.uppy.setupAttachments((file, upload, mediaType) => {
      let attachment = onAttachmentUpload(
        this.store,
        this.postType === PostType.GLOBAL_NOTICE ? 3 : 2,
        this.computedSchool,
        this.get('session.person'),
        file,
        upload,
        mediaType,
      );
      this.attachments = [...this.attachments, attachment];
    });
  }
}
